import { Vector2 } from 'three';

import type { CutoutInfo } from '../types';

export interface CreateRectangularCutoutParams {
	id: string;
	islandIndex: number;
	width: number;
	height: number;
	depth: number;
	position: { x: number; y: number; z: number };
}

/**
 * Create a rectangular cutout
 */
export const createRectangularCutoutCfn = (
	{ id, islandIndex, width, height, depth, position }: CreateRectangularCutoutParams
): CutoutInfo => {
	const halfWidth = width / 2;
	const halfHeight = height / 2;

	return {
		id,
		islandPositionIndex: islandIndex,
		points: [
			new Vector2(-halfWidth, -halfHeight),
			new Vector2(halfWidth, -halfHeight),
			new Vector2(halfWidth, halfHeight),
			new Vector2(-halfWidth, halfHeight)
		],
		depth,
		position
	};
};

export interface CreateCircularCutoutParams {
	id: string;
	islandIndex: number;
	radius: number;
	depth: number;
	position: { x: number; y: number; z: number };
	segments?: number;
}

/**
 * Create a circular cutout (approximated with segments)
 */
export const createCircularCutoutCfn = (
	{ id, islandIndex, radius, depth, position, segments = 32 }: CreateCircularCutoutParams
): CutoutInfo => {
	const points: Vector2[] = [];

	for (let i = 0; i < segments; i++) {
		const angle = (i / segments) * Math.PI * 2;
		points.push(new Vector2(
			Math.cos(angle) * radius,
			Math.sin(angle) * radius
		));
	}

	return {
		id,
		islandPositionIndex: islandIndex,
		points,
		depth,
		position
	};
};

export interface CreateArchCutoutParams {
	id: string;
	islandIndex: number;
	width: number;
	height: number;
	depth: number;
	position: { x: number; y: number; z: number };
	/** Fraction of the height at which the straight sides stop and the arc springs. */
	springRatio?: number;
	segments?: number;
}

/**
 * Create an arched cutout: straight sides up to the springing line, then a circular arc
 * to the apex. The outline is a vertical profile (width x height) and must be extruded
 * horizontally, so the returned cutout sets `verticalProfile`.
 */
export const createArchCutoutCfn = (
	{ id, islandIndex, width, height, depth, position, springRatio = 0.78, segments = 24 }: CreateArchCutoutParams
): CutoutInfo => {
	const halfWidth = width / 2;
	const spring = height * springRatio;
	// circle through (-halfWidth, spring), (0, height) and (halfWidth, spring)
	const rise = height - spring;
	const radius = ((halfWidth * halfWidth) + (rise * rise)) / (2 * rise);
	const centreY = height - radius;

	const points: Array<Vector2> = [
		new Vector2(-halfWidth, 0),
		new Vector2(-halfWidth, spring)
	];
	const a0 = Math.atan2(spring - centreY, -halfWidth);
	const a1 = Math.atan2(spring - centreY, halfWidth);
	for (let i = 1; i < segments; i++) {
		const a = a0 + (((a1 - a0) * i) / segments);
		points.push(new Vector2(Math.cos(a) * radius, centreY + (Math.sin(a) * radius)));
	}
	points.push(new Vector2(halfWidth, spring), new Vector2(halfWidth, 0));

	return {
		id,
		islandPositionIndex: islandIndex,
		points,
		depth,
		position,
		verticalProfile: true
	};
};
